import { useCallback, useEffect, useMemo, useState } from 'react';

import { isEqual, omit } from 'lodash-es';

import { IStore } from '@rbi-ctg/store';
import { useLDContext } from 'state/launchdarkly';
import { useServiceModeContext } from 'state/service-mode';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { initStore2_0 } from 'utils/preload-store';
import { remapStore } from 'utils/restaurant';

import {
  IPreloadedStoreState2_0,
  IStoreContext,
  IUseAnyStore,
  SelectedStore,
  Store
} from './types';
import { resetLastTimeStoreUpdated } from './use-store-utils';

const preloadedStoreInfo = (): IPreloadedStoreState2_0 | null =>
  LocalStorage.getItem(StorageKeys.STORE_VERSION_2);

/**
 * Store Selection 2.0
 *
 * A user will be able to select any store, regardless
 * of availability.
 *
 * This hook will expose the availability of the store so that
 * we may hide/show content based on the store's state.
 */
export const useAnyStore = (): IUseAnyStore => {
  const { updateUserStore } = useLDContext();
  const { serviceMode } = useServiceModeContext();
  const preloaded = useMemo(() => preloadedStoreInfo(), []);
  const [store, _setStore] = useState(initStore2_0(preloaded, serviceMode));
  const remappedStore = useMemo(() => remapStore(store), [store]);

  const setStore = useCallback(
    (newStore: SelectedStore | Store | ((prevState: Store) => Store)) =>
      _setStore(s => {
        // remove `hasSelection` from store object
        const updatedStore = (newStore && 'hasSelection' in newStore
          ? newStore
          : omit(newStore, 'hasSelection')) as Store | ((prevState: Store) => Store);

        const value = typeof updatedStore === 'function' ? updatedStore(s) : updatedStore;

        if (isEqual(value, s)) {
          return s;
        }

        return value;
      }),
    []
  );

  const resetStore = useCallback<IStoreContext['resetStore']>(() => setStore(null), [setStore]);

  const onConfirmStoreChange = useCallback(
    async ({ newStore, callback }) => {
      setStore(newStore);
      resetLastTimeStoreUpdated();

      if (typeof callback === 'function') {
        await callback();
      }
    },
    [setStore]
  );

  useEffect(() => {
    LocalStorage.setItem(StorageKeys.STORE_VERSION_2, {
      store
    });
    // Update the user store in LD
    updateUserStore(store);
  }, [store, updateUserStore]);

  const storeProxy = useMemo(
    () => ({
      frontCounterClosed: store?.frontCounterClosed ?? null,
      hasTakeOut: store?.hasTakeOut ?? null,
      hasDineIn: store?.hasDineIn ?? null,
      _id: store?._id ?? null,
      deliveryHours: store?.deliveryHours ?? null,
      diningRoomHours: store?.diningRoomHours ?? null,
      drinkStationType: store?.drinkStationType ?? null,
      driveThruHours: store?.driveThruHours ?? null,
      driveThruLaneType: store?.driveThruLaneType ?? null,
      curbsideHours: store?.curbsideHours ?? null,
      email: store?.email ?? null,
      environment: store?.environment ?? null,
      franchiseGroupId: store?.franchiseGroupId ?? null,
      franchiseGroupName: store?.franchiseGroupName ?? null,
      hasBreakfast: store?.hasBreakfast ?? null,
      hasBurgersForBreakfast: store?.hasBurgersForBreakfast ?? null,
      hasCurbside: store?.hasCurbside ?? null,
      hasCatering: store?.hasCatering ?? null,
      hasDelivery: store?.hasDelivery ?? null,
      hasDriveThru: store?.hasDriveThru ?? null,
      hasMobileOrdering: store?.hasMobileOrdering ?? null,
      hasParking: store?.hasParking ?? null,
      hasPlayground: store?.hasPlayground ?? null,
      hasWifi: store?.hasWifi ?? null,
      isDarkKitchen: store?.isDarkKitchen ?? null,
      latitude: store?.latitude ?? null,
      longitude: store?.longitude ?? null,
      mobileOrderingStatus: store?.mobileOrderingStatus ?? null,
      name: store?.name ?? null,
      number: store?.number ?? null,
      parkingType: store?.parkingType ?? null,
      phoneNumber: store?.phoneNumber ?? null,
      physicalAddress: store?.physicalAddress ?? null,
      playgroundType: store?.playgroundType ?? null,
      pos: store?.pos ?? null,
      posRestaurantId: store?.posRestaurantId ?? null,
      restaurantImage: store?.restaurantImage ?? null,
      restaurantPosData: store?.restaurantPosData ?? null,
      status: store?.status ?? null,
      chaseMerchantId: store?.chaseMerchantId ?? null,
      distanceInMiles: store?.distanceInMiles ?? null
    }),
    [store]
  );

  return useMemo(
    () => ({
      onConfirmStoreChange,
      store: remappedStore,
      resetStore,
      setStore,
      storeProxy,
      // Store selection 2.0 allows selection of any store
      selectUnavailableStore: (newStore: IStore & { hasSelection: false }) => setStore(newStore)
    }),
    [onConfirmStoreChange, remappedStore, resetStore, setStore, storeProxy]
  );
};
